import React, { useEffect, useState } from "react";
import {
  Users,
  HeartPulse,
  Building2,
  ShieldCheck,
  Tags,
  ScrollText,
  UserCheck,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import KPICard from "@/components/common/KPICard";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { useT } from "@/i18n/I18nContext";
import { api } from "@/lib/api";

const PIE_COLORS = ["#0B7E8C", "#128C97", "#06B6D4", "#6366F1", "#F59E0B", "#94A3B8", "#10B981", "#EF4444"];

function ActionFormat({ action }) {
  return <StatusBadge status={action.toLowerCase()} label={action} />;
}

export default function Dashboard() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).finally(() => setLoading(false));
  }, []);

  const k = data?.kpis || {};

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="System Admin Command Center"
        subtitle="What's happening across the Hospital @ Home network — right now."
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard testId="kpi-total-users" icon={Users} label="Total Users" value={k.total_users ?? "—"} trendLabel={`${k.active_users ?? 0} ${t("active")}`} accent="#0B7E8C" />
        <KPICard testId="kpi-active-patients" icon={HeartPulse} label="Active Patients" value={k.active_patients ?? "—"} trendLabel={`${t("of")} ${k.total_patients ?? 0} ${t("total")}`} accent="#06B6D4" />
        <KPICard testId="kpi-facilities" icon={Building2} label="Facilities" value={k.total_facilities ?? "—"} trendLabel={`${k.active_facilities ?? 0} ${t("operational")}`} accent="#6366F1" />
        <KPICard testId="kpi-audit-today" icon={Activity} label="Audit Events Today" value={k.audit_today ?? "—"} trendLabel={t("Last 24 hours")} accent="#F59E0B" />
      </div>

      {/* Second KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <KPICard testId="kpi-active-users" icon={UserCheck} label="Active Users" value={k.active_users ?? "—"} accent="#10B981" />
        <KPICard testId="kpi-total-roles" icon={ShieldCheck} label="Total Roles" value={k.total_roles ?? "—"} accent="#0B7E8C" />
        <KPICard testId="kpi-total-lookups" icon={Tags} label="Lookup Categories" value={k.total_lookups ?? "—"} accent="#6366F1" />
        <KPICard testId="kpi-total-patients" icon={ScrollText} label="Total Patients" value={k.total_patients ?? "—"} accent="#128C97" />
      </div>

      {/* Mid section: charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Audit trend */}
        <div className="hah-card p-5 lg:col-span-2" data-testid="audit-trend-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="hah-section-title">Trends</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Audit events — last 7 days</h3>
            </div>
            <div className="text-xs text-[#6B7280]">Live</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.audit_trend || []}>
                <defs>
                  <linearGradient id="auditGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0B7E8C" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0B7E8C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="day" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <Area type="monotone" dataKey="events" stroke="#0B7E8C" strokeWidth={2} fill="url(#auditGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User distribution */}
        <div className="hah-card p-5" data-testid="user-distribution-card">
          <div className="hah-section-title">Workforce</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">User distribution by role</h3>
          <div className="h-64 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.user_distribution || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {(data?.user_distribution || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {(data?.user_distribution || []).slice(0, 6).map((row, i) => (
              <div key={row.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-[#1F2937] capitalize">{row.name?.replace(/_/g, " ")}</span>
                </div>
                <span className="font-medium text-[#1F2937]">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Third row: operational + system health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="hah-card p-5" data-testid="operational-card">
          <div className="hah-section-title">What needs attention</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Operational queue</h3>
          <ul className="mt-4 space-y-3">
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[#1F2937]">
                <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                Locked user accounts
              </span>
              <span className="text-lg font-semibold text-[#1F2937]">{data?.operational?.locked_users ?? 0}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[#1F2937]">
                <Clock className="w-4 h-4 text-[#F59E0B]" />
                Inactive facilities
              </span>
              <span className="text-lg font-semibold text-[#1F2937]">{data?.operational?.inactive_facilities ?? 0}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[#1F2937]">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                Patients discharged today
              </span>
              <span className="text-lg font-semibold text-[#1F2937]">{data?.operational?.discharged_today ?? 0}</span>
            </li>
          </ul>
        </div>

        <div className="hah-card p-5" data-testid="facility-distribution-card">
          <div className="hah-section-title">Network</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Facility distribution</h3>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.facility_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} />
                <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
                <Bar dataKey="value" fill="#0B7E8C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hah-card p-5" data-testid="system-health-card">
          <div className="hah-section-title">What will happen next</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">System health</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {Object.entries(data?.system_health || {}).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="capitalize text-[#1F2937]">{k.replace(/_/g, " ")}</span>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-[#10B98114] text-[#10B981]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                  {v}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Recent activities + audit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="hah-card p-5" data-testid="recent-activities-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="hah-section-title">Activity feed</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Recent activities</h3>
            </div>
          </div>
          <ul className="space-y-3">
            {(data?.recent_activities || []).map((a) => (
              <li key={a.id} className="flex items-start gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold shrink-0">
                  {(a.user_email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#1F2937] font-medium">{a.user_email || "system"}</span>
                    <ActionFormat action={a.action} />
                    <span className="text-[#6B7280] text-xs capitalize">{a.entity_type}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">
                    {a.entity_label} • {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
            {(!data?.recent_activities || data.recent_activities.length === 0) && (
              <li className="text-sm text-[#6B7280]">No recent activity.</li>
            )}
          </ul>
        </div>

        <div className="hah-card p-5" data-testid="recent-audit-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="hah-section-title">Compliance</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Latest audit log</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <th className="py-2">Time</th>
                  <th className="py-2">Action</th>
                  <th className="py-2">Entity</th>
                  <th className="py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_audit || []).map((a) => (
                  <tr key={a.id} className="border-t border-[#E5E7EB]">
                    <td className="py-2 text-[#6B7280] text-xs">{new Date(a.created_at).toLocaleTimeString()}</td>
                    <td className="py-2"><ActionFormat action={a.action} /></td>
                    <td className="py-2 text-[#1F2937] capitalize">{a.entity_type}</td>
                    <td className="py-2 text-[#6B7280]">{a.user_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            {(!data?.recent_audit || data.recent_audit.length === 0) && (
              <div className="text-sm text-[#6B7280] py-4">No audit events.</div>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-6 text-sm text-[#6B7280]" data-testid="dashboard-loading">Loading dashboard data…</div>
      )}
    </div>
  );
}
